import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { MANAGER_ROUTES } from "../../utils/constants";
import { Add, ArrowBack, ExpandMore, Remove } from "@material-ui/icons";
import { useIntl } from "react-intl";
import { common } from "../../messages";
import { wikiMuseo } from "../../messages/wikiMuseo";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";

const useStyles = makeStyles(() => ({
  accordionDetails: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  fullWidth: {
    width: "100%",
  },
  redIcon: {
    color: "red",
  },
  cardActionsRight: {
    justifyContent: "flex-end",
    marginTop: "1rem",
  },
}));

export default function Question() {
  const classes = useStyles();
  const intl = useIntl();
  const { bmapi, notifyError, startLoading, stopLoading } = useBmapi();
  const [values, setValues] = useState({
    category: "",
    correct_explication: "",
    incorrect_explication: "",
    options: [{ text: "", points: 0 }],
    points: 0,
    scoring: "",
    text: "",
    type: "",
  });
  const [categories, setCategories] = useState([]);
  const [expandedBlock, setExpandedBlock] = useState(false);

  useEffect(() => {
    loadCategoryList();
  }, [loadCategoryList]);

  const loadCategoryList = useCallback(() => {
    startLoading();

    bmapi
      .getGenListCategoriesQuiz()
      .then((resp) => {
        setCategories(resp.items ?? []);
      })
      .catch((e) => {
        if (
          e.httpCode === 404 &&
          getErrorMessageString(e) === "Entity not found"
        ) {
          return;
        }

        notifyError(getErrorMessageString(e, intl));
      })
      .finally(() => {
        stopLoading();
      });
  }, [bmapi, intl, notifyError, startLoading, stopLoading]);

  // const handleValue = useCallback(
  //   (varName) => (e) => {
  //     ((val) => setValues((v) => ({ ...v, [varName]: val })))(e.target.value);
  //   },
  //   []
  // );

  // const handleValue = useCallback(
  //   (varName) => (e) => {
  //     setValues((v) => ({ ...v, [varName]: e.target.value }));
  //   },
  //   []
  // );

  const handleValue = useCallback(
    (varName, index = null, subField = null) => (e) => {
      let value = e.target.value;

      if (e.target.type === "number") {
        value = value === "" ? "" : Number(value);
      }

      setValues((v) => {
        if (index === null) {
          return { ...v, [varName]: value };
        }

        const updatedArray = [...v[varName]];
        updatedArray[index] = {
          ...updatedArray[index],
          [subField]: value,
        };

        return { ...v, [varName]: updatedArray };
      });
    },
    []
  );

  const handleExpandBlock = (panel) => (event, isExpanded) => {
    setExpandedBlock(isExpanded ? panel : false);
  };

  const addOption = () => {
    setValues((prev) => ({
      ...prev,
      options: [...prev.options, { text: "", points: 0 }],
    }));
  };

  const removeOption = (index) => {
    setValues((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const onConfirm = (e) => {
    e.preventDefault();
    console.log(values);
  };

  return (
    <Container maxWidth="sm">
      <Box mt={2}>
        <Button
          component={RouterLink}
          to={MANAGER_ROUTES.QUIZ}
          startIcon={<ArrowBack />}
        >
          {intl.formatMessage(common.back)}
        </Button>
      </Box>

      <Box mb={2}>
        <Card>
          <CardContent>
            <form onSubmit={onConfirm}>
              <Box mb={1}>
                <TextField
                  fullWidth
                  id="question-type"
                  label={intl.formatMessage(common.typeOf)}
                  margin="dense"
                  name="type"
                  onChange={handleValue("type")}
                  required
                  select
                  value={values.type}
                >
                  <MenuItem value="s">Risposta singola</MenuItem>
                  <MenuItem value="m">Risposta multipla</MenuItem>
                  <MenuItem value="c">Completamento</MenuItem>
                </TextField>
              </Box>

              <Box mb={1}>
                <TextField
                  fullWidth
                  id="question-text"
                  label={intl.formatMessage(wikiMuseo.question)}
                  margin="dense"
                  multiline
                  onChange={handleValue("text")}
                  required
                  type="text"
                  value={values.text}
                />
              </Box>

              <Box mb={2}>
                <TextField
                  fullWidth
                  id="question-category"
                  label={intl.formatMessage(common.category)}
                  margin="dense"
                  name="category"
                  onChange={handleValue("category")}
                  select
                  value={values.category}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Nessuna categoria</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.str_value}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box mb={1}>
                <FormControl component="fieldset">
                  <Typography variant="caption">
                    {intl.formatMessage(common.score)}
                  </Typography>
                  <RadioGroup
                    onChange={handleValue("scoring")}
                    required
                    row
                    value={values.scoring}
                  >
                    <FormControlLabel
                      value="sxq"
                      control={<Radio required color="primary" size="small" />}
                      label={intl.formatMessage(common.pointsPerQuestion)}
                    />
                    <FormControlLabel
                      value="sxo"
                      control={<Radio required color="primary" size="small" />}
                      label={intl.formatMessage(common.pointsPerAnswer)}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {values.scoring === "sxq" && (
                <Box mb={1}>
                  <TextField
                    fullWidth
                    id="question-points"
                    label={intl.formatMessage(common.questionPoints)}
                    margin="dense"
                    name="question-points"
                    onChange={handleValue("points")}
                    required
                    type="number"
                    value={values.points}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
              )}

              <Box my={4}>
                <Typography variant="caption">
                  Se una delle due spiegazioni sotto è assente mostra l’altra
                  sia per risposta corretta che sbagliata.
                </Typography>

                <TextField
                  fullWidth
                  id="correct-explication-text"
                  label={intl.formatMessage(common.correctExplication)}
                  margin="dense"
                  multiline
                  onChange={handleValue("correct_explication")}
                  required={!values.incorrect_explication}
                  type="text"
                  value={values.correct_explication}
                />

                <TextField
                  fullWidth
                  id="incorrect-explication-text"
                  label={intl.formatMessage(common.incorrectExplication)}
                  margin="dense"
                  multiline
                  onChange={handleValue("incorrect_explication")}
                  required={!values.correct_explication}
                  type="text"
                  value={values.incorrect_explication}
                />
              </Box>

              <Box mt={2}>
                <Typography gutterBottom>
                  {intl.formatMessage(wikiMuseo.answers)}
                </Typography>

                <Button
                  variant="text"
                  size="small"
                  startIcon={<Add fontSize="small" />}
                  onClick={() => addOption()}
                >
                  {intl.formatMessage(wikiMuseo.answerAdd)}
                </Button>

                {values.options.map((option, index) => (
                  <Accordion
                    expanded={expandedBlock === index}
                    onChange={handleExpandBlock(index)}
                    key={index}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore fontSize="small" />}
                      aria-controls={`group${index + 1}-content`}
                      id={`group${index + 1}-header`}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                          className={classes.redIcon}
                          edge="start"
                          aria-label="delete"
                          onClick={() => removeOption(index)}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <Typography>{`Risposta ${index + 1}`}</Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails className={classes.accordionDetails}>
                      <Box>
                        <TextField
                          id="answer-text"
                          label={intl.formatMessage(wikiMuseo.answer)}
                          margin="dense"
                          multiline
                          name="answer-text"
                          onChange={handleValue("options", index, "text")}
                          required
                          type="text"
                          value={option.text}
                          fullWidth
                        />
                        {values.scoring === "sxo" && (
                          <TextField
                            id="answer-points"
                            label={intl.formatMessage(common.answerPoints)}
                            margin="dense"
                            name="answer-points"
                            onChange={handleValue("options", index, "points")}
                            required
                            type="number"
                            value={option.points}
                            fullWidth
                          />
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
              <CardActions className={classes.cardActionsRight}>
                <Button type="submit" variant="contained" color="primary">
                  {intl.formatMessage(common.save)}
                </Button>
              </CardActions>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
